import { c as _c } from "react/compiler-runtime";
import { FALLBACK_ROUTE, STEP_MAP, isInStepMap, useNavigateWorkflow } from '@data/hooks/registration/useNavigateWorkflow';
import { Data, RegistrationWorkflowNavigatorOptions, WorkflowStep, useRegistrationWorkflowNavigator } from '@data/hooks/registration/useRegistrationWorkflowNavigator';
import { extendQueryResult } from '@data/queries/util/extendQueryResult';
import { setCurrentRegistrationUser } from '@data/store/slices/currentRegistrationUser/setters';
import { UseQueryResult } from '@tanstack/react-query';
import { logError } from '@util/logger';
import safelySerializeWithType from '@util/safelySerializeWithType';
import { useEffect } from 'react';
const getNextStepRoute = (data: TWorkflowData): TRoute => {
  const {
    canAccess,
    moveTo
  } = data;
  if (canAccess || moveTo === null) {
    return FALLBACK_ROUTE;
  }
  if (!isInStepMap(moveTo)) {
    logError(`Invalid workflow step: ${safelySerializeWithType(moveTo)}`, {
      metadata: {
        moveTo,
        caller: 'getNextStepRoute',
        workflowData: data
      }
    });
    return FALLBACK_ROUTE;
  }
  return STEP_MAP[moveTo];
};
type Result = UseQueryResult<Data, ApiError> & {
  navigateWorkflow(workflow: TWorkflowData): void;
  getNextStepRoute(workflow: TWorkflowData): TRoute;
};
export type WorkflowOptions = {
  blockRedirection?: boolean;
  queryOptions?: RegistrationWorkflowNavigatorOptions;
};
export const useRegistrationWorkflow = (step, options) => {
  const $ = _c(20);
  const queryResult = useRegistrationWorkflowNavigator(step, options?.queryOptions);
  const {
    data,
    status
  } = queryResult;
  const navigateWorkflow = useNavigateWorkflow();
  const cannotAccess = status !== "success" || !data.workflow.canAccess;
  const exceptionallyRedirectToSplashPage = step === "localcommunity" && data?.workflow.moveTo === "end";
  let t0;
  if ($[0] !== cannotAccess || $[1] !== data || $[2] !== exceptionallyRedirectToSplashPage || $[3] !== navigateWorkflow || $[4] !== options?.blockRedirection) {
    t0 = () => {
      if (data && cannotAccess && !options?.blockRedirection) {
        if (exceptionallyRedirectToSplashPage) {
          navigateWorkflow({
            ...data.workflow,
            moveTo: "interests_submitted"
          });
          return;
        }
        navigateWorkflow(data.workflow);
        return;
      }
    };
    $[0] = cannotAccess;
    $[1] = data;
    $[2] = exceptionallyRedirectToSplashPage;
    $[3] = navigateWorkflow;
    $[4] = options?.blockRedirection;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const t1 = options?.blockRedirection;
  let t2;
  if ($[6] !== cannotAccess || $[7] !== data || $[8] !== exceptionallyRedirectToSplashPage || $[9] !== navigateWorkflow || $[10] !== status || $[11] !== t1) {
    t2 = [t1, data, status, navigateWorkflow, exceptionallyRedirectToSplashPage, cannotAccess];
    $[6] = cannotAccess;
    $[7] = data;
    $[8] = exceptionallyRedirectToSplashPage;
    $[9] = navigateWorkflow;
    $[10] = status;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t0, t2);
  let t3;
  if ($[13] !== queryResult.data) {
    t3 = () => {
      if (queryResult.data?.user) {
        setCurrentRegistrationUser(queryResult.data.user);
      }
    };
    $[13] = queryResult.data;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = queryResult.data?.user;
  let t5;
  if ($[15] !== t4) {
    t5 = [t4];
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useEffect(t3, t5);
  let t6;
  if ($[17] !== navigateWorkflow || $[18] !== queryResult) {
    t6 = extendQueryResult(queryResult, {
      navigateWorkflow,
      getNextStepRoute
    });
    $[17] = navigateWorkflow;
    $[18] = queryResult;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const result = t6;
  return result;
};